/**
 * Analytics: plug-and-play. Nothing loads unless env vars are set.
 *
 * Options (choose one — or use both):
 *   REACT_APP_PLAUSIBLE_DOMAIN=which-media.com     (privacy-first — no cookies, no consent needed)
 *   REACT_APP_PLAUSIBLE_HOST=https://plausible.io  (optional; for self-hosters)
 *   REACT_APP_GA_ID=G-XXXXXXXXXX                    (GA4 — REQUIRES cookie consent in the EU)
 *
 * The site is a React Router SPA so we manually fire a page view on every route change.
 */
const PLAUSIBLE_DOMAIN = process.env.REACT_APP_PLAUSIBLE_DOMAIN;
const PLAUSIBLE_HOST = (process.env.REACT_APP_PLAUSIBLE_HOST || "https://plausible.io").replace(/\/$/, "");
const GA_ID = process.env.REACT_APP_GA_ID;
const CONSENT_KEY = "wm_cookie_consent"; // "accepted" | "declined" | null

let plausibleLoaded = false;
let gaLoaded = false;

const injectScript = (src, attrs = {}) => {
  if (document.querySelector(`script[src="${src}"]`)) return;
  const s = document.createElement("script");
  s.src = src;
  s.defer = true;
  Object.entries(attrs).forEach(([k, v]) => s.setAttribute(k, v));
  document.head.appendChild(s);
};

const loadPlausible = () => {
  if (!PLAUSIBLE_DOMAIN || plausibleLoaded) return;
  injectScript(`${PLAUSIBLE_HOST}/js/script.manual.js`, { "data-domain": PLAUSIBLE_DOMAIN });
  window.plausible =
    window.plausible ||
    function () {
      (window.plausible.q = window.plausible.q || []).push(arguments);
    };
  plausibleLoaded = true;
};

export const loadGA = () => {
  if (!GA_ID || gaLoaded) return;
  injectScript(`https://www.googletagmanager.com/gtag/js?id=${GA_ID}`);
  window.dataLayer = window.dataLayer || [];
  window.gtag = function () {
    window.dataLayer.push(arguments);
  };
  window.gtag("js", new Date());
  window.gtag("config", GA_ID, { send_page_view: false, anonymize_ip: true });
  gaLoaded = true;
};

// Plausible is cookieless → load immediately.
loadPlausible();

// GA requires consent — only load if the user has previously accepted.
export const gaConsentRequired = Boolean(GA_ID);
export const getConsent = () => {
  try { return localStorage.getItem(CONSENT_KEY); } catch { return null; }
};
export const setConsent = (choice) => {
  try { localStorage.setItem(CONSENT_KEY, choice); } catch {}
  if (choice === "accepted") loadGA();
};

if (gaConsentRequired && getConsent() === "accepted") loadGA();

/** Fire a pageview to whichever providers are enabled. Called on route change. */
export const trackPageview = (path) => {
  const url = path || window.location.pathname + window.location.search;
  if (plausibleLoaded && window.plausible) {
    window.plausible("pageview", { u: window.location.origin + url });
  }
  if (gaLoaded && window.gtag) {
    window.gtag("event", "page_view", { page_path: url, page_location: window.location.origin + url });
  }
};

export const analyticsActive = plausibleLoaded || gaLoaded;
